import { logger } from 'src/lib/logger'

export const slugify = (text: string): string => {
  try {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '') // Trim - from end of text
  } catch (error) {
    logger.error('Error slugifying text:', error)
    return ''
  }
}

/**
 * Safely gets a property value with optional chaining
 * @param obj The object to access
 * @param path Array of property keys to access
 * @returns The value or null if any part of the path is undefined
 */
export const safeGet = (obj: any, path: string[]): any => {
  try {
    return path.reduce((current, key) => current?.[key], obj) ?? null
  } catch (error) {
    logger.error('Error in safeGet:', error, { obj, path })
    return null
  }
}

/**
 * Creates a clean input object with only defined values
 * Filters out undefined, null, and empty string values unless they're explicitly allowed
 * @param input The input object to clean
 * @param allowedNullKeys Keys that are allowed to have null values
 * @returns Clean input object
 */
export const createCleanInput = (
  input: Record<string, any>,
  allowedNullKeys: string[] = []
): Record<string, any> => {
  const cleanInput: Record<string, any> = {}

  for (const [key, value] of Object.entries(input)) {
    // Skip undefined values
    if (value === undefined) {
      continue
    }

    // Allow null values for specified keys
    if (value === null && allowedNullKeys.includes(key)) {
      cleanInput[key] = value
      continue
    }

    // Skip null values for non-allowed keys
    if (value === null) {
      continue
    }

    // Handle strings - trim and skip empty unless allowed
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed || allowedNullKeys.includes(key)) {
        cleanInput[key] = trimmed
      }
      continue
    }

    // Handle booleans - only include if explicitly true or false
    if (typeof value === 'boolean') {
      cleanInput[key] = value
      continue
    }

    // Handle arrays - only include if not empty
    if (Array.isArray(value) && value.length > 0) {
      cleanInput[key] = value
      continue
    }

    // Include other truthy values (numbers, objects, etc.)
    if (value) {
      cleanInput[key] = value
    }
  }

  return cleanInput
}

/**
 * Safely checks if a property has a non-null value
 * @param propertyObj The property object to check
 * @returns boolean indicating if the property has a value
 */
export const hasPropertyValue = (propertyObj: any): boolean => {
  return safeGet(propertyObj, ['value', 'value']) !== null
}

export const isConnectionError = (error: any): boolean => {
  if (!error) return false

  // Check error code
  if (error.code === 'ECONNRESET' || error.code === 'EPIPE') return true

  // Check error message
  const errorMessage = error.message || ''
  return (
    errorMessage.includes('ECONNRESET') ||
    errorMessage.includes('Connection reset') ||
    errorMessage.includes('Network error') ||
    // Check for the specific error format from lambda
    (errorMessage.includes('read ECONNRESET') && error.code === 'ECONNRESET')
  )
}
